import React from 'react'
import PropTypes from 'prop-types'
import { Map, List } from 'immutable'
import classNames from 'classnames'
import { PureDataComponent } from '@aytm/lib/common/components/pure-component'
import { stopEvent, DOMchildOf, DOMfindParent, viewport } from '@aytm/lib/common/func'
import {
  KEYCODE_UP,
  KEYCODE_DOWN,
  KEYCODE_PGUP,
  KEYCODE_PGDOWN,
  KEYCODE_HOME,
  KEYCODE_END,
  KEYCODE_ENTER,
  KEYCODE_SPACE,
  KEYCODE_ESC,
  KEYCODE_TAB
} from '@aytm/lib/common/const'
import { fuzzyMatch } from '@aytm/lib/common/string'
import LoaderWave from '@aytm/lib/rfd-ui/components/basic/loader-wave'
import LoaderDots from '@aytm/lib/rfd-ui/components/basic/loader-dots'

import '@aytm/lib/rfd-ui/styles/basic/select.scss'

const MAX_OPTIONS_NUMBER = 100
// const DEFAULT_LIST_MAXHEIGHT = 200

let nextSelectID = 0

export const SELECT_FILTER_STRATEGIES = {
  default: (text, filter /*, option*/) => [text.includes(filter), 1],
  fuzzy: (text, filter /*, option*/) => {
    const [longestMatchedSequenceLength] = fuzzyMatch(text, filter)
    return [Boolean(longestMatchedSequenceLength), longestMatchedSequenceLength]
  },
  firstChar: (text, filter, option) => {
    const [defaultMatch, defaultPriority] = SELECT_FILTER_STRATEGIES.default(text, filter, option)
    if (defaultMatch) {
      return [true, defaultPriority]
    }
    const firstChars = text
      .split(/[\s(),.:[\]-]+/)
      .map(w => w.charAt(0))
      .join('')
    return [0 === firstChars.indexOf(filter), 2]
  }
}

export default class Select extends PureDataComponent {
  static propTypes = {
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    placeholder: PropTypes.string,
    options: PropTypes.oneOfType([PropTypes.instanceOf(List), PropTypes.func]).isRequired,
    opened: PropTypes.bool,
    value: PropTypes.any,
    onSelect: PropTypes.func,
    onClose: PropTypes.func,
    onFocus: PropTypes.func,
    isEmpty: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    filterOptions: PropTypes.bool,
    filterPlaceholder: PropTypes.string,
    filterStrategy: PropTypes.oneOf(Object.keys(SELECT_FILTER_STRATEGIES)),
    descriptionOptions: PropTypes.bool,
    matchOptions: PropTypes.bool,
    buttonText: PropTypes.func,
    matchNoPlaceholder: PropTypes.string,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    drop: PropTypes.string,
    theme: PropTypes.string,
    onOverflow: PropTypes.func,
    noScroll: PropTypes.bool,
    optionsMinHeight: PropTypes.number,
    optionsMaxHeight: PropTypes.number,
    showSelected: PropTypes.bool,
    allowReselection: PropTypes.bool,
    fullScreenOptions: PropTypes.bool,
    newIcon: PropTypes.bool,
    icon: PropTypes.string,
    dataTestId: PropTypes.string
  }

  constructor(props) {
    super(props)
    this.addGlobalCloseEvents = this.addGlobalCloseEvents.bind(this)
    this.focusOn = this.focusOn.bind(this)
    this.toggleOptions = this.toggleOptions.bind(this)
    this.selectOption = this.selectOption.bind(this)
    this.clickOptionList = this.clickOptionList.bind(this)
    this.mouseEnterOptions = this.mouseEnterOptions.bind(this)
    this.mouseLeavedOptions = this.mouseLeavedOptions.bind(this)
    this.mouseEnterOption = this.mouseEnterOption.bind(this)
    this.closeOptionsList = this.closeOptionsList.bind(this)
    this.setValueOption = this.setValueOption.bind(this)
    this.selectedOptionIndex = this.selectedOptionIndex.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.scrollToOption = this.scrollToOption.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onListScroll = this.onListScroll.bind(this)
    this.filteredOptions = this.filteredOptions.bind(this)
    this.defaultIsEmpty = this.defaultIsEmpty.bind(this)
    this.fetchSelectOptions = this.fetchSelectOptions.bind(this)
    this.getText = this.getText.bind(this)
    this.renderButtonText = this.renderButtonText.bind(this)
    this.renderButton = this.renderButton.bind(this)
    this.state = {
      opened: false,
      valueOption: Map({ value: null, text: '' }),
      filter: '',
      keyboardIndex: null,
      focused: false,
      flipped: false,
      page: 1,
      fetchedOptions: List()
    }
    this.filterChangeDelayTimer = null
    this.mouseOverOptions = false
    this.filteredOptionsCache = null
    this.filteredOptionsCacheFor = null
    this.overflow = 0
    this.loading = false
    this.selectID = nextSelectID++
  }

  addGlobalCloseEvents() {
    if ('function' == typeof this.removeGlobalEvents) {
      this.removeGlobalEvents()
    }
    const mouseDownHandler = event => {
      if (this.refRoot && DOMchildOf(event.target, this.refRoot)) return
      this.closeOptionsList()
    }

    this.removeGlobalEvents = () => {
      ;(this.refRoot?.ownerDocument || document).removeEventListener('mousedown', mouseDownHandler)
      this.removeGlobalEvents = null
    }
    ;(this.refRoot?.ownerDocument || document).addEventListener('mousedown', mouseDownHandler)
  }

  focusOn(ref) {
    const el = this[`ref${ref}`]
    if (el && el.focus) {
      el.focus()
      return true
    }
    return false
  }

  toggleOptions(event) {
    const { opened, options, value, showSelected, onFocus } = this.props
    void (event && stopEvent(event))
    const { opened: sOpened, valueOption } = this.state
    if (null == opened) {
      const willBeOpened = !sOpened
      if (willBeOpened && 'function' == typeof options) {
        options((fetchedOptions = List()) => {
          this.filteredOptionsCache = null
          this.filteredOptionsCacheFor = null
          this.setValueOption('undefined' == typeof value ? valueOption.get('value') : value, fetchedOptions)
          this.setState({ fetchedOptions })
        })
      }
      this.setState({
        opened: willBeOpened,
        filter: '',
        keyboardIndex: null
      })
      if (willBeOpened) {
        this.addGlobalCloseEvents()
        setTimeout(() => {
          if (this.mounted) {
            if ('function' === typeof onFocus) {
              onFocus()
            }
            this.focusOn('Filter') || this.focusOn('FocusHolder')
          }
        }, 0)
        const selIndex = showSelected
          ? this.selectedOptionIndex(valueOption.get('value'))
          : this.selectedOptionIndex(valueOption.get('value')) > 1
            ? this.selectedOptionIndex(valueOption.get('value')) - 1
            : 0
        if (selIndex >= 0) {
          this.setState({ keyboardIndex: selIndex })
          setTimeout(() => {
            if (this.mounted) this.scrollToOption(selIndex)
          }, 0)
        }
      } else {
        if ('function' == typeof this.removeGlobalEvents) this.removeGlobalEvents()
        setTimeout(() => {
          if (this.mounted) this.focusOn('FocusHolder')
        }, 0)
      }
    } else this.focusOn('FocusHolder')
    return false
  }

  selectOption(option) {
    const { allowReselection } = this.props
    this.filteredOptionsCache = null
    if (Map.isMap(option)) {
      const { valueOption } = this.state
      if (option != valueOption || allowReselection) {
        const { onSelect, placeholder } = this.props
        if ('function' == typeof onSelect) {
          const res = onSelect(option)
          this.setState({
            valueOption: false == res ? Map({ value: null, text: placeholder }) : option
          })
        } else {
          this.setState({ valueOption: option })
        }
      }
    }
  }

  clickOptionList(event) {
    stopEvent(event)
    const optionElement = event.target
      ? DOMfindParent(event.target, i => i.className && i.className.split(/\s+/).includes('rfd-ui-select-option'), 32)
      : null
    const optionIndex = optionElement ? optionElement.dataset.index : null
    if (null != optionIndex) {
      const optionIndexInt = parseInt(optionIndex, 10)
      if (!isNaN(optionIndexInt)) {
        const option = this.filteredOptions().get(optionIndexInt)
        if (option && !option.get('disabled')) {
          this.closeOptionsList(true)
          this.selectOption(option)
          // to prevent from being highlighted when no keyboard used:
          setTimeout(() => {
            this.setState({ focused: false })
          }, 0)
        }
      }
    }
    return false
  }

  mouseEnterOptions() {
    this.mouseOverOptions = true
  }

  mouseLeavedOptions() {
    this.mouseOverOptions = false
  }

  mouseEnterOption(event) {
    const optionElement = event.target
      ? DOMfindParent(event.target, i => i.className && i.className.split(/\s+/).includes('rfd-ui-select-option'), 32)
      : null
    const optionIndex = optionElement ? optionElement.dataset.index : null

    null != optionIndex && this.setState({ keyboardIndex: optionIndex })
  }

  closeOptionsList(refocus = false) {
    this.setState({ page: 1 })
    const { opened, onClose } = this.props
    if (null == opened) {
      if ('function' == typeof this.removeGlobalEvents) this.removeGlobalEvents()
      this.setState({
        opened: false,
        filter: '',
        optionIndex: null,
        keyboardIndex: null,
        focused: refocus
      })
      this.mouseOverOptions = false
      if (refocus) {
        setTimeout(() => {
          if (this.mounted) this.focusOn('FocusHolder')
        }, 0)
      }
      if ('function' === typeof onClose) {
        onClose()
      }
    }
  }

  setValueOption(
    value = this.props.value || Map(),
    options = this.fetchSelectOptions(),
    placeholder = this.props.placeholder
  ) {
    this.setState({
      valueOption: options.find(o => o.get('value') == value, null, Map({ value: null, text: placeholder || '' }))
    })
  }

  selectedOptionIndex(value = this.props.value || Map(), options = this.fetchSelectOptions()) {
    return options.findIndex(o => o.get('value') == value)
  }

  onFilterChange(event) {
    const value = event.target ? event.target.value : ''
    if (this.filterChangeDelayTimer) {
      clearTimeout(this.filterChangeDelayTimer)
    }
    this.filterChangeDelayTimer = setTimeout(() => {
      if (!this.mounted) return
      this.filterChangeDelayTimer = null
      this.setState({ filter: value, keyboardIndex: null, page: 1 })
    }, 180)
  }

  scrollToOption(index) {
    const list = this.refOptions
    if (list) {
      const listItems = list.children
      if (listItems) {
        const item = listItems[index]
        if (item) {
          try {
            const listScrollTop = list.scrollTop
            const itemTop = item.offsetTop - list.offsetTop
            if (itemTop < listScrollTop) {
              list.scrollTop = itemTop
            } else if (itemTop >= listScrollTop) {
              list.scrollTop = Math.max(0, itemTop)
            }
          } catch (error) {
            window.console && window.console.error && window.console.error('scrollToOption', index, error)
          }
        }
      }
    }
  }

  onKeyDown(event) {
    switch (event.keyCode) {
      case KEYCODE_UP: {
        stopEvent(event)
        if (!this.mouseOverOptions) {
          const { keyboardIndex } = this.state
          const options = this.filteredOptions()
          const newKeyboardIndex =
            null == keyboardIndex ? 0 : Math.min(options.count(), Math.max(-1, keyboardIndex - 1))
          this.setState({ keyboardIndex: newKeyboardIndex })
          this.scrollToOption(newKeyboardIndex)
        }
        return false
      }
      case KEYCODE_DOWN: {
        stopEvent(event)
        if (!this.mouseOverOptions) {
          const { keyboardIndex } = this.state
          const options = this.filteredOptions()
          const newKeyboardIndex =
            null == keyboardIndex ? 0 : Math.min(options.count(), Math.max(-1, keyboardIndex + 1))
          this.setState({ keyboardIndex: newKeyboardIndex })
          this.scrollToOption(newKeyboardIndex)
          this.onListScroll()
        }
        return false
      }
      case KEYCODE_PGUP: {
        stopEvent(event)
        if (!this.mouseOverOptions) {
          const { keyboardIndex } = this.state
          const options = this.filteredOptions()
          const newKeyboardIndex =
            null == keyboardIndex ? 0 : Math.min(options.count() - 1, Math.max(0, keyboardIndex - 7))
          this.setState({ keyboardIndex: newKeyboardIndex })
          this.scrollToOption(newKeyboardIndex)
        }
        return false
      }
      case KEYCODE_PGDOWN: {
        stopEvent(event)
        if (!this.mouseOverOptions) {
          const { keyboardIndex } = this.state
          const options = this.filteredOptions()
          const newKeyboardIndex =
            null == keyboardIndex ? 0 : Math.min(options.count() - 1, Math.max(0, keyboardIndex + 7))
          this.setState({ keyboardIndex: newKeyboardIndex })
          this.scrollToOption(newKeyboardIndex)
          this.onListScroll()
        }
        return false
      }
      case KEYCODE_HOME: {
        stopEvent(event)
        if (!this.mouseOverOptions) {
          this.setState({ keyboardIndex: 0 })
          this.scrollToOption(0)
        }
        return false
      }
      case KEYCODE_END: {
        stopEvent(event)
        if (!this.mouseOverOptions) {
          const options = this.filteredOptions()
          const newKeyboardIndex = options.count() - 1
          this.setState({ keyboardIndex: newKeyboardIndex })
          this.scrollToOption(newKeyboardIndex)
          this.onListScroll()
        }
        return false
      }
      case KEYCODE_ENTER: {
        const { keyboardIndex } = this.state
        const opened = null == this.props.opened ? this.state.opened : this.props.opened
        if (opened && null != keyboardIndex && keyboardIndex >= 0) {
          const option = this.filteredOptions().get(keyboardIndex)
          if (option) {
            this.closeOptionsList(true)
            this.selectOption(option)
          }
        } else if (!opened) {
          this.toggleOptions()
        } else if (opened) {
          this.setState({ keyboardIndex: 0 })
          this.scrollToOption(0)
        }
        break
      }
      case KEYCODE_SPACE: {
        const opened = null == this.props.opened ? this.state.opened : this.props.opened
        if (!opened) {
          this.toggleOptions()
        }
        break
      }
      case KEYCODE_ESC: {
        if (this.state.opened) {
          this.closeOptionsList(true)
        } else {
          this.setState({ focused: false }) // double esc removes highlight
        }
        break
      }
      case KEYCODE_TAB: {
        if (this.state.opened) {
          this.closeOptionsList()
        }
        break
      }
    }
    return true
  }

  onFocus() {
    this.setState({ focused: true })
  }

  onBlur() {
    this.setState({ focused: false })
  }

  onListScroll() {
    const options = this.fetchSelectOptions()
    if (
      this.state.opened &&
      this.refOptions.scrollHeight - this.refOptions.scrollTop - 1 <= this.refOptions.clientHeight &&
      !this.loading &&
      options.size > this.state.page * MAX_OPTIONS_NUMBER
    ) {
      this.loading = true
      const index = this.state.keyboardIndex
      setTimeout(() => {
        if (this.state.keyboardIndex > index) {
          this.setState({ keyboardIndex: index })
        }
        this.loading = false
        this.setState({ page: this.state.page + 1 })
      }, 500)
    }
  }

  filteredOptions(filter = this.state.filter) {
    const options = this.fetchSelectOptions()
    const { filterOptions, matchOptions, showSelected, filterStrategy = 'default' } = this.props
    const { valueOption } = this.state
    if (!filterOptions && showSelected && options.size <= MAX_OPTIONS_NUMBER) return options
    if (
      null != this.filteredOptionsCache &&
      filter == this.filteredOptionsCacheFor &&
      options.size <= MAX_OPTIONS_NUMBER
    ) {
      return this.filteredOptionsCache
    }
    const lcfilter = filter.toLocaleLowerCase ? filter.toLocaleLowerCase() : filter.toLowerCase()

    const filterStrategyFn = SELECT_FILTER_STRATEGIES[filterStrategy] || SELECT_FILTER_STRATEGIES.default
    const filterPriorities = new window.Map()

    const filtered = options
      .filter(o => {
        const text = o.has('text') ? String(o.get('text')) : ''
        const lctext = text.toLocaleLowerCase ? text.toLocaleLowerCase() : text.toLowerCase()
        const [match, priority] = lcfilter ? filterStrategyFn(lctext, lcfilter, o) : [true, 0]
        if (match) {
          filterPriorities.set(o, priority)
        }
        return (
          (match || (o.has('skipFilter') && o.get('skipFilter'))) &&
          (showSelected || //show or hide selected item
            valueOption.get('value') != o.get('value'))
        )
      })
      .map(o => {
        if (matchOptions && lcfilter.length) {
          const text = o.has('text') ? String(o.get('text')) : ''
          const lcText = text.toLocaleLowerCase ? text.toLocaleLowerCase() : text.toLowerCase()
          const lcIndex = lcText.indexOf(lcfilter)
          const matchedText = text.slice(lcIndex, lcIndex + lcfilter.length)
          return o.set(
            'text',
            text.replace(matchedText, `<span class="rfd-ui-select-option-text-matched">${matchedText}</span>`)
          )
        } else {
          return o
        }
      })
      .sort((a, b) => {
        const priorityA = filterPriorities.get(a) || 0
        const priorityB = filterPriorities.get(b) || 0
        return priorityB - priorityA
      })

    this.filteredOptionsCache = filtered
    this.filteredOptionsCacheFor = filter
    return filtered
  }

  defaultIsEmpty(value) {
    return null == value.get('value')
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    super.UNSAFE_componentWillReceiveProps(nextProps)
    if (nextProps.options != this.props.options || nextProps.value != this.props.value) {
      this.setValueOption(nextProps.value, this.fetchSelectOptions(nextProps.options), nextProps.placeholder)
    }
    if (nextProps.options != this.props.options) {
      this.filteredOptionsCache = null
      this.filteredOptionsCacheFor = null
    }
    if (null != nextProps.opened && nextProps.opened != this.props.opened) {
      this.setState({ opened: nextProps.opened })
    }
    if (nextProps.empty) {
      this.setState({
        valueOption: Map({ value: null, text: nextProps.placeholder || '' })
      })
    }
  }

  UNSAFE_componentWillMount() {
    super.UNSAFE_componentWillMount()
    this.filteredOptionsCache = null
    this.filteredOptionsCacheFor = null
  }

  componentDidMount() {
    super.componentDidMount()
    this.setValueOption()
  }

  componentWillUnmount() {
    super.componentWillUnmount()
    if ('function' == typeof this.removeGlobalEvents) {
      this.removeGlobalEvents()
    }
    this.filteredOptionsCache = null
    this.filteredOptionsCacheFor = null
  }

  fetchSelectOptions(options = this.props.options) {
    const { fetchedOptions } = this.state
    return ('function' == typeof options ? fetchedOptions : options) || List()
  }

  getText(valueOption) {
    const { buttonText } = this.props
    const optionText = valueOption.get('text') || valueOption.get('listText') || ''
    return 'function' == typeof buttonText ? buttonText(optionText) : optionText
  }

  renderButtonText(valueOption) {
    const optionCategory = valueOption.get('category')
    return (
      <span
        className="rfd-ui-select-button-text"
        dangerouslySetInnerHTML={{
          __html: (optionCategory ? `<b>${optionCategory}: </b>` : '') + this.getText(valueOption)
        }}
      />
    )
  }

  renderButton() {
    const options = this.fetchSelectOptions()
    const { placeholder, filterPlaceholder, disabled, icon, fetching, newIcon } = this.props
    const { valueOption } = this.state
    const filterOptions = this.props.filterOptions || options.size > MAX_OPTIONS_NUMBER
    const opened = disabled ? false : null == this.props.opened ? this.state.opened : this.props.opened

    return !opened || (opened && !filterOptions) ? (
      <div
        className="rfd-ui-select-button"
        id={`rfd-ui-select-${this.selectID}`}
        tabIndex={0}
        role="combobox"
        aria-haspopup="listbox"
        aria-controls={`rfd-ui-select-${this.selectID}-panel`}
        aria-expanded="false"
        aria-label={this.getText(valueOption)}
        ref={this.saveRef('FocusHolder')}
        onMouseDown={this.toggleOptions}
        onKeyDown={this.onKeyDown}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
      >
        {fetching ? (
          <div className="rfd-ui-select-button-loader">
            <LoaderDots dots={3} dotSize={4} height={16} />
          </div>
        ) : (
          <>
            {valueOption.get('icon') && <i className={`icon-aytmfd-${valueOption.get('icon')}`} />}
            {icon && <i className={`icon-aytmfd-${icon}`} />}
            {this.renderButtonText(valueOption)}
            {(options.size > 0 || 'function' == typeof this.props.options) &&
              (newIcon ? (
                <span className="icon-aytmfd-chevron-bracket" />
              ) : (
                <span className="rfd-ui-select-button-arrow" />
              ))}
          </>
        )}
      </div>
    ) : (
      <div className="rfd-ui-select-filter">
        <input
          type="text"
          placeholder={filterPlaceholder || placeholder}
          role="combobox"
          aria-haspopup="listbox"
          aria-controls={`rfd-ui-select-${this.selectID}-panel`}
          aria-expanded="true"
          aria-label={this.getText(valueOption)}
          ref={this.saveRef('Filter')}
          onChange={this.onFilterChange}
          onKeyDown={this.onKeyDown}
        />
      </div>
    )
  }

  renderOptions() {
    const options = this.fetchSelectOptions()
    const {
      width,
      disabled,
      noScroll,
      fullScreenOptions,
      smallHeight,
      descriptionOptions,
      matchOptions,
      matchNoPlaceholder,
      optionsMinHeight,
      optionsMaxHeight
    } = this.props
    const { valueOption, keyboardIndex, filter, page } = this.state
    const opened = disabled ? false : null == this.props.opened ? this.state.opened : this.props.opened
    const filteredOptions = this.filteredOptions()
    const loading = MAX_OPTIONS_NUMBER * page < filteredOptions.size

    const optionsHeight = fullScreenOptions && opened ? viewport().h - 40 : 'none'

    const DOMoptions =
      opened && options.size > 0
        ? filteredOptions.takeWhile((_x, i) => i <= Math.min(options.size, MAX_OPTIONS_NUMBER * page - 1))
        : List()

    const countedWidth = width ? (isNaN(Number(width)) ? width : Number(width)) : 200
    const containerWidth = isNaN(Number(countedWidth)) ? 'none' : countedWidth / (descriptionOptions ? 2 : 1)

    return opened && options.size > 0 ? (
      <div
        className="rfd-ui-select-container"
        style={{
          width: containerWidth
        }}
      >
        <ul
          className="rfd-ui-select-options"
          role="listbox"
          id={`rfd-ui-select-${this.selectID}-panel`}
          onMouseDown={this.clickOptionList}
          onMouseEnter={this.mouseEnterOptions}
          onMouseLeave={this.mouseLeavedOptions}
          onScroll={this.onListScroll}
          style={{
            height: optionsHeight,
            minHeight:
              null != optionsMinHeight
                ? `${optionsMinHeight}px`
                : noScroll || fullScreenOptions
                  ? 'none'
                  : descriptionOptions
                    ? smallHeight
                      ? '115px'
                      : '200px'
                    : optionsHeight,
            maxHeight:
              null != optionsMaxHeight
                ? `${optionsMaxHeight}px`
                : noScroll || fullScreenOptions
                  ? 'none'
                  : smallHeight
                    ? '115px'
                    : '200px'
          }}
          ref={this.saveRef('Options')}
        >
          {(() => {
            const DOMoptions = filteredOptions
              .takeWhile((_x, i) => i <= Math.min(options.size, MAX_OPTIONS_NUMBER * page - 1))
              .map((o, oi) => {
                const selected = valueOption.get('value') == o.get('value')
                const ariaSelected = null != keyboardIndex && 0 <= keyboardIndex ? keyboardIndex == oi : selected
                const icon = o.get(selected ? 'selectedIcon' : 'icon')
                return (
                  <li
                    className={classNames('rfd-ui-select-option', {
                      'rfd-ui-select-option-selected': selected,
                      'rfd-ui-select-option-current': keyboardIndex == oi,
                      'rfd-ui-select-option-first': 0 == oi,
                      'rfd-ui-select-option-disabled': o.get('disabled')
                    })}
                    role="option"
                    aria-selected={ariaSelected}
                    aria-activedescendant={ariaSelected ? `rfd-ui-select-${this.selectID}` : null}
                    aria-label={o.get('text')}
                    onMouseEnter={this.mouseEnterOption}
                    data-index={oi}
                    key={oi}
                  >
                    <div
                      className="rfd-ui-select-option-text"
                      dangerouslySetInnerHTML={{
                        __html:
                          (icon ? `<i class='icon-aytmfd-${icon}'></i>` : '') +
                          (o.get('category') ? `<b>${o.get('category')}: </b>` : '') +
                          (o.get('listText') || o.get('text'))
                      }}
                    />
                  </li>
                )
              })
            return (
              loading
                ? DOMoptions.push(
                    <li className="rfd-ui-select-option rfd-ui-loading-sign" data-index={-1} key={-1}>
                      <span className="rfd-ui-select-option-text rfd-ui-select-loader">
                        <LoaderWave bars={5} height={10} />
                      </span>
                    </li>
                  )
                : filter.length && matchOptions && matchNoPlaceholder.length && !DOMoptions.size
                  ? DOMoptions.push(
                      <li className="rfd-ui-select-option" data-index={-1} key={-1}>
                        <div
                          className="rfd-ui-select-option-text"
                          dangerouslySetInnerHTML={{
                            __html: matchNoPlaceholder
                          }}
                        />
                      </li>
                    )
                  : DOMoptions
            ).toArray()
          })()}
        </ul>
        {descriptionOptions &&
          (() => {
            const option = DOMoptions.get(keyboardIndex)
            return (
              <div
                className="rfd-ui-select-description"
                dangerouslySetInnerHTML={{
                  __html: (option && option.get('description')) || ''
                }}
                style={{
                  width: containerWidth
                }}
              />
            )
          })()}
      </div>
    ) : null
  }

  render() {
    const options = this.fetchSelectOptions()
    const { width, error, disabled, drop, theme, fullScreenOptions, className, dataTestId } = this.props
    const { valueOption, flipped } = this.state
    const opened = disabled ? false : null == this.props.opened ? this.state.opened : this.props.opened
    const focused = !disabled && (this.state.focused || this.state.opened)

    const isEmpty =
      'function' == typeof this.props.isEmpty
        ? this.props.isEmpty(valueOption, options)
        : null == this.props.isEmpty
          ? this.defaultIsEmpty(valueOption, options)
          : Boolean(this.props.isEmpty)

    return (
      <div
        className={classNames('rfd-ui-select', className, {
          'rfd-ui-select-opened': opened,
          'rfd-ui-select-focused': focused,
          'rfd-ui-select-error': error,
          'rfd-ui-select-disabled': disabled,
          'rfd-ui-select-empty': isEmpty,
          'rfd-ui-select-mobile': fullScreenOptions && !opened,
          'rfd-ui-select-fullscreen': fullScreenOptions && opened,
          'rfd-ui-select-up': 'up' == drop || flipped,
          [`rfd-ui-select-theme-${theme}`]: theme
        })}
        data-testid={dataTestId}
        style={fullScreenOptions && opened ? {} : { width: width ? width : 200 }}
        ref={this.saveRef('Root')}
      >
        {this.renderButton()}
        {this.renderOptions()}
      </div>
    )
  }
}
